import { Component, EventEmitter, Input, OnInit, OnChanges, Output, AfterViewInit, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

export interface Model3DObject {
    id: string;
    name: string;
    type: 'room' | 'floor' | 'building' | 'furniture' | 'asset';
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number };
    scale: { x: number; y: number; z: number };
    color?: string;
    texture?: string;
    isSelected?: boolean;
    metadata?: any;
}

export interface Model3DScene {
    objects: Model3DObject[];
    lighting: {
        ambient: number;
        directional: { intensity: number; position: { x: number; y: number; z: number } };
    };
    camera: {
        position: { x: number; y: number; z: number };
        target: { x: number; y: number; z: number };
    };
}

@Component({
    selector: 'app-3d-model-viewer',
    templateUrl: './3d-model-viewer.component.html',
    styleUrls: ['./3d-model-viewer.component.scss']
})
export class ThreeDModelViewerComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @Input() modelType: 'building' | 'tooth' | 'floor' = 'building';
    @Input() modelData: any = null;
    @Input() isOpen = false;
    @Input() width = '100%';
    @Input() height = '400px';
    @Input() showControls = true;
    @Input() allowSelection = true;
    @Input() showLabels = true;
    @Output() objectSelected = new EventEmitter<Model3DObject>();
    @Output() sceneChanged = new EventEmitter<Model3DScene>();
    @Output() closeViewer = new EventEmitter<void>();

    @ViewChild('viewer3d', { static: true }) viewer3dRef: ElementRef<HTMLCanvasElement>;

    private languageSubscription: Subscription;

    scene: Model3DScene = {
        objects: [],
        lighting: {
            ambient: 0.4,
            directional: { intensity: 0.6, position: { x: 10, y: 10, z: 10 } }
        },
        camera: {
            position: { x: 0, y: 5, z: 10 },
            target: { x: 0, y: 0, z: 0 }
        }
    };

    // 3D Model controls — start with isometric view for clear 3D perception
    rotationX = 30;
    rotationY = -35;
    rotationZ = 0;
    scale = 1;
    isDragging = false;
    lastMouseX = 0;
    lastMouseY = 0;
    selectedObject: Model3DObject | null = null;

    // View modes
    viewMode: 'rotate' | 'select' | 'edit' = 'rotate';
    showGrid = true;
    showAxes = false;
    wireframeMode = false;

    // Translation cache for better performance
    translations: any = {};

    constructor(private _translate: TranslateService, private _cdr: ChangeDetectorRef) {
        // Initialize default translations immediately
        this.initializeDefaultTranslations();

        // Subscribe to language changes
        this.languageSubscription = this._translate.onLangChange.subscribe((event) => {
            console.log('Language changed to:', event.lang);
            if (event.lang === 'ar') {
                this.loadArabicTranslations();
            } else {
                this.initializeDefaultTranslations();
            }
            this._cdr.detectChanges();
        });
    }

    ngOnInit(): void {
        this.initializeScene();

        // Load translations based on current language
        const currentLang = this._translate.currentLang || this._translate.defaultLang || 'en';
        console.log('Component initialized with language:', currentLang);

        if (currentLang === 'ar') {
            this.loadArabicTranslations();
        } else {
            // Make sure we have English translations
            this.initializeDefaultTranslations();
        }

        this._cdr.detectChanges();
    }

    ngOnChanges(): void {
        if (this.modelData && this.isOpen) {
            this.loadModelData();
        }
    }

    ngAfterViewInit(): void {
        this.initializeViewer();
    }

    ngOnDestroy(): void {
        if (this.languageSubscription) {
            this.languageSubscription.unsubscribe();
        }
    }

    private initializeScene(): void {
        switch (this.modelType) {
            case 'building':
                this.createBuildingScene();
                break;
            case 'floor':
                this.createFloorScene();
                break;
            case 'tooth':
                this.createToothScene();
                break;
        }
    }

    private createBuildingScene(): void {
        this.scene.objects = [
            {
                id: 'building-main',
                name: 'Main Building',
                type: 'building',
                position: { x: 0, y: 0, z: 0 },
                rotation: { x: 0, y: 0, z: 0 },
                scale: { x: 1, y: 1, z: 1 },
                color: '#8B4513',
                metadata: { floors: 3, rooms: 20 }
            },
            {
                id: 'floor-1',
                name: 'Ground Floor',
                type: 'floor',
                position: { x: 0, y: 0, z: 0 },
                rotation: { x: 0, y: 0, z: 0 },
                scale: { x: 10, y: 0.2, z: 8 },
                color: '#D2691E'
            },
            {
                id: 'floor-2',
                name: 'First Floor',
                type: 'floor',
                position: { x: 0, y: 3, z: 0 },
                rotation: { x: 0, y: 0, z: 0 },
                scale: { x: 10, y: 0.2, z: 8 },
                color: '#CD853F'
            },
            {
                id: 'floor-3',
                name: 'Second Floor',
                type: 'floor',
                position: { x: 0, y: 6, z: 0 },
                rotation: { x: 0, y: 0, z: 0 },
                scale: { x: 10, y: 0.2, z: 8 },
                color: '#DEB887'
            }
        ];
    }

    private createFloorScene(): void {
        this.scene.objects = [
            {
                id: 'floor-base',
                name: 'Floor Base',
                type: 'floor',
                position: { x: 0, y: 0, z: 0 },
                rotation: { x: 0, y: 0, z: 0 },
                scale: { x: 12, y: 0.1, z: 10 },
                color: '#F5F5DC'
            },
            {
                id: 'room-1',
                name: 'Reception',
                type: 'room',
                position: { x: -4, y: 1.5, z: -3 },
                rotation: { x: 0, y: 0, z: 0 },
                scale: { x: 3, y: 3, z: 2 },
                color: '#87CEEB'
            },
            {
                id: 'room-2',
                name: 'Dining Area',
                type: 'room',
                position: { x: 2, y: 1.5, z: -3 },
                rotation: { x: 0, y: 0, z: 0 },
                scale: { x: 4, y: 3, z: 3 },
                color: '#98FB98'
            },
            {
                id: 'room-3',
                name: 'Kitchen',
                type: 'room',
                position: { x: -2, y: 1.5, z: 2 },
                rotation: { x: 0, y: 0, z: 0 },
                scale: { x: 3, y: 3, z: 2 },
                color: '#FFB6C1'
            }
        ];
    }

    private createToothScene(): void {
        // Keep existing tooth scene logic for dental applications
        this.scene.objects = [
            {
                id: 'tooth-crown',
                name: 'Crown',
                type: 'asset',
                position: { x: 0, y: 0, z: 0 },
                rotation: { x: 0, y: 0, z: 0 },
                scale: { x: 1, y: 1, z: 1 },
                color: '#FFFFFF'
            }
        ];
    }

    private loadModelData(): void {
        if (this.modelData) {
            // Override scene with provided model data
            if (this.modelData.objects) {
                this.scene.objects = this.modelData.objects;
            }
            if (this.modelData.camera) {
                this.scene.camera = this.modelData.camera;
            }
            if (this.modelData.lighting) {
                this.scene.lighting = this.modelData.lighting;
            }
        }
    }

    private initializeViewer(): void {
        // Initialize 3D rendering context (simplified CSS 3D transforms for now)
        // In a real implementation, you would use Three.js, Babylon.js, or WebGL directly
        this.renderScene();
    }

    private renderScene(): void {
        // This is a simplified implementation using CSS 3D transforms
        // For production, you would want to use a proper 3D engine like Three.js
    }

    // Mouse interaction handlers
    onMouseDown(event: MouseEvent): void {
        if (this.viewMode === 'rotate') {
            this.isDragging = true;
            this.lastMouseX = event.clientX;
            this.lastMouseY = event.clientY;
            event.preventDefault();
        }
    }

    onMouseMove(event: MouseEvent): void {
        if (this.isDragging && this.viewMode === 'rotate') {
            const deltaX = event.clientX - this.lastMouseX;
            const deltaY = event.clientY - this.lastMouseY;

            this.rotationY += deltaX * 0.5;
            this.rotationX += deltaY * 0.5;

            // Clamp rotation values
            this.rotationX = Math.max(-90, Math.min(90, this.rotationX));
            this.rotationY = this.rotationY % 360;

            this.lastMouseX = event.clientX;
            this.lastMouseY = event.clientY;
        }
    }

    onMouseUp(): void {
        this.isDragging = false;
    }

    onWheel(event: WheelEvent): void {
        event.preventDefault();
        const delta = event.deltaY * 0.001;
        this.scale = Math.max(0.1, Math.min(3, this.scale - delta));
    }

    onObjectClick(object: Model3DObject): void {
        if (this.allowSelection && this.viewMode === 'select') {
            this.selectedObject = object;
            object.isSelected = true;

            // Deselect other objects
            this.scene.objects.forEach(obj => {
                if (obj.id !== object.id) {
                    obj.isSelected = false;
                }
            });

            this.objectSelected.emit(object);
        }
    }

    // View controls
    toggleViewMode(): void {
        const modes: ('rotate' | 'select' | 'edit')[] = ['rotate', 'select', 'edit'];
        const currentIndex = modes.indexOf(this.viewMode);
        this.viewMode = modes[(currentIndex + 1) % modes.length];
    }

    resetView(): void {
        this.rotationX = 30;
        this.rotationY = -35;
        this.rotationZ = 0;
        this.scale = 1;
    }

    toggleGrid(): void {
        this.showGrid = !this.showGrid;
    }

    toggleAxes(): void {
        this.showAxes = !this.showAxes;
    }

    toggleWireframe(): void {
        this.wireframeMode = !this.wireframeMode;
    }

    // Preset views
    setTopView(): void {
        this.rotationX = 90;
        this.rotationY = 0;
        this.rotationZ = 0;
    }

    setFrontView(): void {
        this.rotationX = 0;
        this.rotationY = 0;
        this.rotationZ = 0;
    }

    setSideView(): void {
        this.rotationX = 0;
        this.rotationY = 90;
        this.rotationZ = 0;
    }

    setIsometricView(): void {
        this.rotationX = 30;
        this.rotationY = 45;
        this.rotationZ = 0;
    }

    // Utility methods
    getSceneTransformStyle(): string {
        return `
      rotateX(${this.rotationX}deg) 
      rotateY(${this.rotationY}deg) 
      rotateZ(${this.rotationZ}deg) 
      scale(${this.scale})
    `;
    }

    getObjectTransformStyle(object: Model3DObject): string {
        return `
      translate3d(${object.position.x * 40}px, ${object.position.y * -40}px, ${object.position.z * 40}px)
      rotateX(${object.rotation.x}deg)
      rotateY(${object.rotation.y}deg)
      rotateZ(${object.rotation.z}deg)
      scaleX(${object.scale.x})
      scaleY(${object.scale.y})
      scaleZ(${object.scale.z})
    `;
    }

    getObjectColorStyle(object: Model3DObject): any {
        return {};
    }

    onClose(): void {
        this.closeViewer.emit();
    }

    trackObject(index: number, object: Model3DObject): string {
        return object.id;
    }

    private loadTranslations(): void {
        // Use a more reliable translation loading approach
        const translationMap = {
            'SHARED.3D_VIEWER.ROTATE_MODE': 'Rotate',
            'SHARED.3D_VIEWER.SELECT_MODE': 'Select',
            'SHARED.3D_VIEWER.EDIT_MODE': 'Edit',
            'SHARED.3D_VIEWER.RESET_VIEW': 'Reset View',
            'SHARED.3D_VIEWER.TOGGLE_GRID': 'Grid',
            'SHARED.3D_VIEWER.TOGGLE_AXES': 'Axes',
            'SHARED.3D_VIEWER.WIREFRAME': 'Wireframe',
            'SHARED.3D_VIEWER.TOP_VIEW': 'Top',
            'SHARED.3D_VIEWER.FRONT_VIEW': 'Front',
            'SHARED.3D_VIEWER.SIDE_VIEW': 'Side',
            'SHARED.3D_VIEWER.ISOMETRIC_VIEW': 'Isometric',
            'SHARED.3D_VIEWER.ROTATE_INSTRUCTIONS': 'Drag to rotate the model, scroll to zoom in/out',
            'SHARED.3D_VIEWER.SELECT_INSTRUCTIONS': 'Click on objects to select and view details',
            'SHARED.3D_VIEWER.EDIT_INSTRUCTIONS': 'Click and drag to modify object positions',
            'SHARED.3D_VIEWER.LOADING_MODEL': 'Loading 3D model...',
            'SHARED.3D_VIEWER.TYPE': 'Type',
            'SHARED.3D_VIEWER.POSITION': 'Position',
            'SHARED.3D_VIEWER.DETAILS': 'Details'
        };

        // Try to get translations from the service, fallback to default values
        Object.keys(translationMap).forEach(key => {
            this._translate.get(key).subscribe({
                next: (translation) => {
                    // Only use translation if it's not the same as the key (meaning it was found)
                    if (translation && translation !== key) {
                        this.translations[key] = translation;
                    } else {
                        this.translations[key] = translationMap[key];
                    }
                    this._cdr.detectChanges();
                },
                error: () => {
                    // Fallback to default value on error
                    this.translations[key] = translationMap[key];
                    this._cdr.detectChanges();
                }
            });
        });
    }

    // Helper method to get translation with fallback
    getTranslation(key: string, fallback = ''): string {
        // First check our cached translations
        const cachedTranslation = this.translations[key];
        if (cachedTranslation && cachedTranslation !== key) {
            return cachedTranslation;
        }

        // Try instant translation from service
        const instantTranslation = this._translate.instant(key);
        if (instantTranslation && instantTranslation !== key) {
            // Cache this translation for future use
            this.translations[key] = instantTranslation;
            return instantTranslation;
        }

        // Return fallback or default translation
        const defaultTranslation = fallback || this.getDefaultTranslation(key);

        // Cache the fallback too
        if (!this.translations[key]) {
            this.translations[key] = defaultTranslation;
        }

        return defaultTranslation;
    }

    private getDefaultTranslation(key: string): string {
        const defaults: Record<string, string> = {
            'SHARED.3D_VIEWER.ROTATE_MODE': 'Rotate',
            'SHARED.3D_VIEWER.SELECT_MODE': 'Select',
            'SHARED.3D_VIEWER.EDIT_MODE': 'Edit',
            'SHARED.3D_VIEWER.RESET_VIEW': 'Reset View',
            'SHARED.3D_VIEWER.TOGGLE_GRID': 'Grid',
            'SHARED.3D_VIEWER.TOGGLE_AXES': 'Axes',
            'SHARED.3D_VIEWER.WIREFRAME': 'Wireframe',
            'SHARED.3D_VIEWER.TOP_VIEW': 'Top',
            'SHARED.3D_VIEWER.FRONT_VIEW': 'Front',
            'SHARED.3D_VIEWER.SIDE_VIEW': 'Side',
            'SHARED.3D_VIEWER.ISOMETRIC_VIEW': 'Isometric',
            'SHARED.3D_VIEWER.ROTATE_INSTRUCTIONS': 'Drag to rotate the model, scroll to zoom in/out',
            'SHARED.3D_VIEWER.SELECT_INSTRUCTIONS': 'Click on objects to select and view details',
            'SHARED.3D_VIEWER.EDIT_INSTRUCTIONS': 'Click and drag to modify object positions',
            'SHARED.3D_VIEWER.LOADING_MODEL': 'Loading 3D model...',
            'SHARED.3D_VIEWER.TYPE': 'Type',
            'SHARED.3D_VIEWER.POSITION': 'Position',
            'SHARED.3D_VIEWER.DETAILS': 'Details'
        };

        return defaults[key] || key.split('.').pop() || 'N/A';
    }

    private initializeDefaultTranslations(): void {
        // Set default English translations immediately
        this.translations = {
            'SHARED.3D_VIEWER.ROTATE_MODE': 'Rotate',
            'SHARED.3D_VIEWER.SELECT_MODE': 'Select',
            'SHARED.3D_VIEWER.EDIT_MODE': 'Edit',
            'SHARED.3D_VIEWER.RESET_VIEW': 'Reset View',
            'SHARED.3D_VIEWER.TOGGLE_GRID': 'Grid',
            'SHARED.3D_VIEWER.TOGGLE_AXES': 'Axes',
            'SHARED.3D_VIEWER.WIREFRAME': 'Wireframe',
            'SHARED.3D_VIEWER.TOP_VIEW': 'Top',
            'SHARED.3D_VIEWER.FRONT_VIEW': 'Front',
            'SHARED.3D_VIEWER.SIDE_VIEW': 'Side',
            'SHARED.3D_VIEWER.ISOMETRIC_VIEW': 'Isometric',
            'SHARED.3D_VIEWER.ROTATE_INSTRUCTIONS': 'Drag to rotate the model, scroll to zoom in/out',
            'SHARED.3D_VIEWER.SELECT_INSTRUCTIONS': 'Click on objects to select and view details',
            'SHARED.3D_VIEWER.EDIT_INSTRUCTIONS': 'Click and drag to modify object positions',
            'SHARED.3D_VIEWER.LOADING_MODEL': 'Loading 3D model...',
            'SHARED.3D_VIEWER.TYPE': 'Type',
            'SHARED.3D_VIEWER.POSITION': 'Position',
            'SHARED.3D_VIEWER.DETAILS': 'Details'
        };
    }

    private loadArabicTranslations(): void {
        // Set Arabic translations
        this.translations = {
            'SHARED.3D_VIEWER.ROTATE_MODE': 'دوران',
            'SHARED.3D_VIEWER.SELECT_MODE': 'تحديد',
            'SHARED.3D_VIEWER.EDIT_MODE': 'تعديل',
            'SHARED.3D_VIEWER.RESET_VIEW': 'إعادة تعيين العرض',
            'SHARED.3D_VIEWER.TOGGLE_GRID': 'الشبكة',
            'SHARED.3D_VIEWER.TOGGLE_AXES': 'المحاور',
            'SHARED.3D_VIEWER.WIREFRAME': 'إطار سلكي',
            'SHARED.3D_VIEWER.TOP_VIEW': 'علوي',
            'SHARED.3D_VIEWER.FRONT_VIEW': 'أمامي',
            'SHARED.3D_VIEWER.SIDE_VIEW': 'جانبي',
            'SHARED.3D_VIEWER.ISOMETRIC_VIEW': 'أيزومتري',
            'SHARED.3D_VIEWER.ROTATE_INSTRUCTIONS': 'اسحب لتدوير النموذج، استخدم العجلة للتكبير/التصغير',
            'SHARED.3D_VIEWER.SELECT_INSTRUCTIONS': 'انقر على الكائنات لتحديدها وعرض التفاصيل',
            'SHARED.3D_VIEWER.EDIT_INSTRUCTIONS': 'انقر واسحب لتعديل مواضع الكائنات',
            'SHARED.3D_VIEWER.LOADING_MODEL': 'جاري تحميل النموذج ثلاثي الأبعاد...',
            'SHARED.3D_VIEWER.TYPE': 'النوع',
            'SHARED.3D_VIEWER.POSITION': 'الموضع',
            'SHARED.3D_VIEWER.DETAILS': 'التفاصيل'
        };
    }

    // Add this method for debugging and force update
    forceUpdateTranslations(): void {
        const currentLang = this._translate.currentLang || this._translate.defaultLang || 'en';
        console.log('=== Force Update Debug Info ===');
        console.log('Current language:', currentLang);
        console.log('Before update - translations:', this.translations);

        // Clear existing translations
        this.translations = {};

        if (currentLang === 'ar') {
            this.loadArabicTranslations();
            console.log('Applied Arabic translations');
        } else {
            this.initializeDefaultTranslations();
            console.log('Applied English translations');
        }

        this._cdr.detectChanges();
        console.log('After update - translations:', this.translations);

        // Test a few keys
        console.log('Test translations:');
        console.log('ROTATE_MODE:', this.getTranslation('SHARED.3D_VIEWER.ROTATE_MODE'));
        console.log('SELECT_MODE:', this.getTranslation('SHARED.3D_VIEWER.SELECT_MODE'));
        console.log('EDIT_MODE:', this.getTranslation('SHARED.3D_VIEWER.EDIT_MODE'));
        console.log('===============================');
    }
}
